// CÓNYUGE Y DEPENDIENTES EN EL IMPORTADOR DE SOLO PÓLIZAS.
//
// Lissette, 2026-09-14, después de que la primera entrega dejara fuera a la
// familia: *"sí, también los cónyuges y dependientes, y que reconozca
// automáticamente cuando es un exportador de Apizeal"*. Y al preguntarle si no
// le servía el importador de Clientes + Pólizas —que ya hace las dos cosas—:
// *"ya Clientes y Póliza reconoce cuando es un Excel de Apizeal; yo hablo de
// Solo Pólizas, que también reconozca, y que llene cónyuge y dependientes
// cuando es un cliente nuevo"*.
//
// Vive en su propio módulo, y no dentro de PoliciesView.jsx, por dos razones:
// la lista es larga y taparía el resto del archivo, y sobre todo porque así una
// prueba puede IMPORTARLA (un .jsx no se puede importar desde una prueba,
// regla 48) y comprobar que las claves que produce el lector de Apizeal son
// EXACTAMENTE las que el importador sabe mapear. Esa correspondencia, escrita a
// mano en dos sitios, es justo la que se rompe sin que nadie se entere.

/** Cuántos dependientes ofrece el importador. El mismo tope que el de clientes. */
export const MAX_DEPENDIENTES_AL_IMPORTAR = 10;

/**
 * Los campos del CÓNYUGE que se pueden mapear.
 *
 * Las claves son LAS MISMAS que emite `parseApizeal` (`spouseFirstName`,
 * `spouseSsn`…), a propósito: así un export de Apizeal se mapea solo, sin una
 * tabla de traducción que mantener. No pueden chocar con nada de póliza —
 * ninguna clave de póliza empieza por `spouse`.
 */
export function camposDelConyuge() {
  const par = (clave, es, en) => ({ key: clave, es: `💑 Cónyuge — ${es}`, en: `💑 Spouse — ${en}` });
  return [
    par('spouseFirstName', 'nombre', 'first name'),
    par('spouseMiddleName', 'segundo nombre', 'middle name'),
    par('spouseLastName', 'apellido', 'last name'),
    par('spouseDob', 'fecha nac.', 'DOB'),
    par('spouseSsn', 'SSN', 'SSN'),
    par('spouseEmail', 'correo', 'email'),
    par('spousePhone', 'teléfono', 'phone'),
    par('spouseGender', 'sexo', 'sex'),
    par('spouseIncome', 'ingreso', 'income'),
    par('spouseImmigrationStatus', 'migratorio', 'immigration'),
    // El PARENTESCO del cónyuge: la palabra con la que el archivo lo nombró
    // ("SPOUSE", "PARTNER", "Esposa"). De ella sale el estado civil con el que
    // se guarda la ficha — casado/a o unión libre — y sin ella un cónyuge entra
    // sin estado civil, o sea invisible (Lissette, 2026-09-15).
    par('spouseRelation', 'parentesco', 'relationship'),
  ];
}

/**
 * Los diez campos de CADA dependiente, con el mismo criterio de claves.
 *
 * Diez dependientes por diez campos son cien opciones más en el desplegable.
 * Es mucho, y es el mismo tope que el importador de clientes: un hogar de doce
 * existe, y ofrecer menos significa perder gente en silencio al importar. El
 * buscador del desplegable (escribir "dep 3") es lo que lo hace manejable.
 */
export function camposDeDependientes() {
  const out = [];
  for (let n = 1; n <= MAX_DEPENDIENTES_AL_IMPORTAR; n++) {
    const par = (sufijo, es, en) => ({
      key: `dep${n}${sufijo}`,
      es: `👶 Dep. ${n} — ${es}`,
      en: `👶 Dep. ${n} — ${en}`,
    });
    out.push(
      par('FirstName', 'nombre', 'first name'),
      par('MiddleName', 'segundo nombre', 'middle name'),
      par('LastName', 'apellido', 'last name'),
      par('FullName', 'nombre completo', 'full name'),
      par('Dob', 'fecha nac.', 'DOB'),
      par('Ssn', 'SSN', 'SSN'),
      par('Relation', 'parentesco', 'relationship'),
      par('Gender', 'sexo', 'sex'),
      par('Income', 'ingreso', 'income'),
      par('Immigration', 'migratorio', 'immigration'),
    );
  }
  return out;
}

/**
 * Las claves del TITULAR que `parseApizeal` emite con otro nombre.
 *
 * El lector de Apizeal se escribió para el importador de Clientes + Pólizas y
 * usa el vocabulario de aquel (`ssn`, `email`, `monthlyPremium`…). En el de
 * pólizas, los campos del titular llevan el prefijo `client` para no chocar con
 * los de la póliza (que ya tienen `status`, `notes`, `tags`…). Esta tabla es el
 * puente, y solo hace falta para el titular y para la póliza: el cónyuge y los
 * dependientes comparten claves y se mapean solos.
 *
 * Una clave de Apizeal que no esté aquí NO se pierde: la columna sigue en la
 * lista con su nombre del archivo y se puede mapear a mano. Lo que se pierde es
 * que salga puesta sola.
 */
export const APIZEAL_A_IMPORTADOR_DE_POLIZAS = {
  // ── Titular ────────────────────────────────────────────────────────────
  fullName: 'clientName',
  phone: 'clientPhone',
  email: 'clientEmail',
  dob: 'clientDob',
  ssn: 'clientSsn',
  gender: 'clientGender',
  annualIncome: 'clientAnnualIncome',
  maritalStatus: 'clientMaritalStatus',
  placeOfBirth: 'clientPlaceOfBirth',
  immigrationStatus: 'clientImmigrationStatus',
  county: 'clientCounty',
  assignedAgent: 'clientAssignedAgent',
  attendedBy: 'clientAttendedBy',
  addressStreet: 'addressStreet',
  addressCity: 'addressCity',
  addressState: 'addressState',
  addressZip: 'addressZip',
  // ── Póliza ─────────────────────────────────────────────────────────────
  carrier: 'carrier',
  plan: 'plan',
  memberId: 'memberId',
  monthlyPremium: 'premium',
  subsidy: 'subsidy',
  effectiveDate: 'effectiveDate',
  renewalDate: 'renewalDate',
  paymentMethod: 'paymentMethod',
  policyStatus: 'status',
  agentLabel: 'agentLabel',
  producerCode: 'producerCode',
  planTotalCost: 'planTotalCost',
  applicationNumber: 'applicationNumber',
  notes: 'notes',
};

/**
 * ¿Qué campo del importador de pólizas le toca a una clave de `parseApizeal`?
 *
 * Devuelve '' cuando no le toca ninguno (una columna cruda del archivo, o una
 * clave que este importador no ofrece). El llamante la deja sin mapear.
 */
export function campoParaClaveDeApizeal(clave) {
  if (!clave) return '';
  if (APIZEAL_A_IMPORTADOR_DE_POLIZAS[clave]) return APIZEAL_A_IMPORTADOR_DE_POLIZAS[clave];
  // El cónyuge y los dependientes comparten nombre en los dos vocabularios.
  if (/^spouse[A-Z]/.test(clave)) return clave;
  if (/^dep\d+[A-Z]/.test(clave)) return clave;
  return '';
}
